import { useState, useEffect, useRef } from "react";
import { MessageCircle, X, Send, Mail, ArrowLeft } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "/assets/logo.png";

/* ===== FAQ Knowledge Base ===== */
const FAQS = [
  {
    q: "What is AirStageX?",
    a: "AirStageX is a luxury private aviation concept that transforms the journey into the main event — a private plane journey paired with an intimate live performance by a world-class A-list artist at 40,000 feet.",
  },
  {
    q: "How do I join the waiting list?",
    a: "Scroll to the bottom of the homepage and tap “Join Our Waitlist.” Share your name, email, location, and annual travel budget. Our team reviews every application personally.",
  },
  {
    q: "Where does AirStageX fly?",
    a: "Current destinations include Qatar, Saudi Arabia, Greece, New York City, Oman, and Abu Dhabi. Each journey departs from London Heathrow. Custom destinations can be arranged on request.",
  },
  {
    q: "Who performs on the flight?",
    a: "Grammy-winning, globally recognised A-list artists, international vocalists, live bands, orchestras, and DJs. You may request a preferred artist during booking.",
  },
  {
    q: "How much does it cost?",
    a: "AirStageX is invitation-only and tailored to each guest. Please share your travel budget on the waitlist form — a Personal Assistant will be in touch with bespoke pricing.",
  },
  {
    q: "Is the hotel stay included?",
    a: "Yes. Each journey includes a four-night stay at a five-star hotel with breakfast, private chauffeur transfers, and a dedicated hair stylist and makeup artist on request.",
  },
  {
    q: "What is included onboard?",
    a: "A three-course fine-dining menu, premium champagne, a live concert, studio-quality headphones, a limited-edition velvet gift pouch, a signed gold-plated boarding pass, and a private meet-and-greet with the artist.",
  },
  {
    q: "When is the launch event?",
    a: "AirStageX launches in Autumn 2026 with an exclusive VIP gala dinner and a live A-list performance, live streamed to a global audience.",
  },
  {
    q: "How do I contact AirStageX?",
    a: "Email enquiries@airstagex.com or tap “Contact us” below to leave us a message. Bookings are by appointment only.",
  },
  {
    q: "Is it private?",
    a: "Absolutely. Full anonymity options are available. Every journey is managed by a dedicated Personal Assistant with the highest level of discretion.",
  },
  {
    q: "How far in advance should I book?",
    a: "We recommend enquiring at least 8–12 weeks before your preferred travel date so we can secure your artist, aircraft, hotel and concierge team. Last-minute private requests are sometimes possible subject to availability.",
  },
  {
    q: "What is the cancellation and refund policy?",
    a: "Cancellation terms are tailored to each journey and shared in your bespoke proposal. As a guide, full refunds are available up to 60 days prior; partial refunds may apply closer to departure. Your Personal Assistant will walk you through the specifics.",
  },
  {
    q: "Is there a dress code?",
    a: "Smart, elevated attire — think black-tie optional or refined cocktail. We want guests to feel the occasion. Your Personal Assistant can advise on the theme for each journey.",
  },
  {
    q: "Can you cater to dietary requirements?",
    a: "Yes. Menus are fully tailored to vegan, vegetarian, halal, kosher, gluten-free and any other intolerances. Share your requirements at booking and our chef will design around them.",
  },
  {
    q: "Are children allowed?",
    a: "AirStageX is an adults-focused experience. Select family journeys can be arranged privately — please reach out via “Contact us” and we will design something appropriate for your group.",
  },
  {
    q: "Can I bring pets?",
    a: "Assistance animals are always welcome. For other pets, case-by-case arrangements can be made on private charter flights. Let us know in advance.",
  },
  {
    q: "What is the baggage allowance?",
    a: "Generous private-aviation allowances apply: typically 2 check-in pieces (32 kg each) plus hand luggage per guest. Oversized items (instruments, garment bags, gifts) can be arranged with your Personal Assistant.",
  },
  {
    q: "How long is each flight?",
    a: "Flight durations vary by destination, typically 4–8 hours. The live performance is timed to mid-flight at cruising altitude, with dining and service choreographed around it.",
  },
  {
    q: "Do you accept group or private charter bookings?",
    a: "Yes. Full-aircraft buyouts for corporate events, anniversaries, weddings and celebrations are available. Tap “Contact us” with your group size and concept and we will build a bespoke proposal.",
  },
  {
    q: "Are gift vouchers available?",
    a: "Bespoke AirStageX gift experiences can be arranged for a partner, family member or VIP client. Please reach out via “Contact us” for pricing and presentation options.",
  },
  {
    q: "Is the experience accessible?",
    a: "Yes. Please share any accessibility requirements at booking — step-free boarding, dietary, medical, sensory or mobility needs — and we will design the journey around them.",
  },
  {
    q: "Are press or media enquiries welcome?",
    a: "Yes — for press, PR and interview requests please use “Contact us” below or email press@airstagex.com and mark your message as PRESS.",
  },
  {
    q: "Do you offer partnerships or sponsorships?",
    a: "We work with a small circle of luxury partners and brands. For partnership, sponsorship or collaboration opportunities please submit details via “Contact us”.",
  },
];

function matchAnswer(text) {
  const query = text.toLowerCase().trim();
  if (!query) return null;

  const scored = FAQS.map((faq) => {
    const qWords = faq.q.toLowerCase().split(/\s+/);
    const aWords = faq.a.toLowerCase().split(/\s+/);
    let score = 0;
    query.split(/\s+/).forEach((w) => {
      if (w.length < 3) return;
      if (qWords.some((qw) => qw.includes(w))) score += 3;
      if (aWords.some((aw) => aw.includes(w))) score += 1;
    });
    return { faq, score };
  }).sort((a, b) => b.score - a.score);

  if (scored[0].score >= 3) return scored[0].faq.a;
  return null;
}

export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("chat"); // "chat" | "contact"
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    {
      from: "bot",
      text: "Welcome to AirStageX. I can answer common questions about our flights, artists, destinations, pricing and booking. If you'd prefer, tap ‘Contact us’ and leave us a message.",
    },
  ]);
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [contactStatus, setContactStatus] = useState("idle"); // idle | loading | success | error
  const [contactError, setContactError] = useState("");
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, open, view]);

  const send = (text) => {
    const clean = (text ?? input).trim();
    if (!clean) return;
    const userMsg = { from: "user", text: clean };
    const answer = matchAnswer(clean);
    const botMsg = {
      from: "bot",
      text:
        answer ||
        "I can't fully answer that from my FAQ. Tap ‘Contact us’ below and leave us a message — we will reply as soon as possible.",
    };
    setMessages((prev) => [...prev, userMsg, botMsg]);
    setInput("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    send();
  };

  const submitContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.message.trim()) {
      setContactStatus("error");
      setContactError("Please fill in all fields.");
      return;
    }
    setContactStatus("loading");
    setContactError("");
    try {
      await axios.post(`${API}/chat-contact`, contactForm);
      setContactStatus("success");
      setContactForm({ name: "", email: "", message: "" });
    } catch (err) {
      setContactStatus("error");
      setContactError(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  const openContact = () => {
    setView("contact");
    setContactStatus("idle");
    setContactError("");
  };

  const backToChat = () => {
    setView("chat");
    setContactStatus("idle");
    setContactError("");
  };

  const suggestions = [
    "How do I join the waiting list?",
    "Where does AirStageX fly?",
    "Who performs on the flight?",
    "How much does it cost?",
    "How far in advance should I book?",
    "What is the dress code?",
  ];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="chatbot-toggle"
        aria-label={open ? "Close chat" : "Open chat"}
        data-testid="chatbot-toggle-btn"
      >
        {open ? <X className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />}
      </button>

      <div
        className={`chatbot-panel ${open ? "open" : ""}`}
        role="dialog"
        aria-hidden={!open}
        data-testid="chatbot-panel"
      >
        <div className="chatbot-header">
          <div className="flex items-center gap-3">
            {view === "contact" && (
              <button
                type="button"
                onClick={backToChat}
                className="text-zinc-400 hover:text-white p-1 transition-colors"
                aria-label="Back to chat"
                data-testid="chatbot-back-btn"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <img src={LOGO} alt="AirStageX" className="h-8" />
            <div>
              <p className="text-white text-sm font-medium tracking-wide">
                {view === "contact" ? "Leave us a message" : "AirStageX Concierge"}
              </p>
              <p className="text-[#007AFF] text-[10px] uppercase tracking-[0.2em]">
                {view === "contact" ? "We'll reply soon" : "Ask us anything"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-zinc-400 hover:text-white p-1 transition-colors"
            aria-label="Close chat"
            data-testid="chatbot-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {view === "chat" && (
          <>
            <div ref={listRef} className="chatbot-messages" data-testid="chatbot-messages">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`chatbot-msg ${m.from === "user" ? "chatbot-msg-user" : "chatbot-msg-bot"}`}
                  data-testid={`chat-msg-${m.from}`}
                >
                  {m.text}
                </div>
              ))}
            </div>

            <div className="chatbot-suggestions">
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => send(s)}
                  className="chatbot-suggestion"
                  data-testid="chatbot-suggestion"
                >
                  {s}
                </button>
              ))}
              <button
                type="button"
                onClick={openContact}
                className="chatbot-suggestion chatbot-suggestion-contact"
                data-testid="chatbot-contact-us-btn"
              >
                <Mail className="w-3 h-3 inline-block mr-1 -mt-0.5" />
                Contact us
              </button>
            </div>

            <form onSubmit={handleSubmit} className="chatbot-input-row">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your question…"
                className="chatbot-input"
                data-testid="chatbot-input"
              />
              <button
                type="submit"
                className="chatbot-send"
                aria-label="Send"
                data-testid="chatbot-send-btn"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </>
        )}

        {view === "contact" && (
          <div className="chatbot-contact-body" ref={listRef}>
            {contactStatus === "success" ? (
              <div className="chatbot-contact-success" data-testid="chatbot-contact-success">
                <div className="w-10 h-10 rounded-full border border-[#007AFF] flex items-center justify-center mb-4 mx-auto">
                  <Mail className="w-4 h-4 text-[#007AFF]" />
                </div>
                <h4 className="text-white text-sm font-medium mb-2 text-center">Message received</h4>
                <p className="text-zinc-300 text-xs leading-relaxed text-center">
                  Thank you — our team will reply to you by email as soon as possible.
                </p>
                <button
                  type="button"
                  onClick={backToChat}
                  className="chatbot-suggestion mt-6 mx-auto block"
                  data-testid="chatbot-contact-back-btn"
                >
                  Back to chat
                </button>
              </div>
            ) : (
              <form onSubmit={submitContact} className="chatbot-contact-form" data-testid="chatbot-contact-form">
                <p className="chatbot-contact-notice" data-testid="chatbot-contact-notice">
                  We are not online at the moment but we will reply as soon as possible.
                </p>
                <label className="chatbot-label">Name</label>
                <input
                  type="text"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  className="chatbot-input chatbot-input-block"
                  placeholder="Your name"
                  data-testid="chatbot-contact-name"
                  required
                />
                <label className="chatbot-label">Email</label>
                <input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="chatbot-input chatbot-input-block"
                  placeholder="you@email.com"
                  data-testid="chatbot-contact-email"
                  required
                />
                <label className="chatbot-label">Message</label>
                <textarea
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  className="chatbot-input chatbot-input-block chatbot-textarea"
                  placeholder="How can we help?"
                  rows={4}
                  data-testid="chatbot-contact-message"
                  required
                />
                {contactStatus === "error" && (
                  <p className="text-red-400 text-xs mt-1" data-testid="chatbot-contact-error">{contactError}</p>
                )}
                <button
                  type="submit"
                  disabled={contactStatus === "loading"}
                  className="chatbot-contact-submit"
                  data-testid="chatbot-contact-submit"
                >
                  {contactStatus === "loading" ? "Sending…" : "Send message"}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </>
  );
}
